import React from 'react'
import { Button, Icon, Container, Heading, Message } from 'lib/ui'
import Link from 'lib/components/Link'

import useSessionUser from 'lib/hooks/useSessionUser'
import SEO from 'lib/components/SEO'
import LocalDateTime from 'lib/components/LocalDateTime'
import {
  buildRecallCanonical,
  buildRecallDescription,
  buildRecallTitle,
} from 'lib/seo/recallMetadata'
import BasicDetails from 'lib/components/Recall/BasicDetails'
import CategoryIcon from 'lib/components/Recall/CategoryIcon'
import CategoryList from 'lib/components/Recall/CategoryList'

import Images from './Images'
import Products from './Products'
import Social from './Social'
import { Recall as RecallType } from '../../../../types/RM'
import { useCtaView } from 'lib/hooks/useCtaView'
import { isSubscriptionActive } from 'lib/util'
import { trackEvent } from 'lib/util/analytics'
import { buildRecallCta } from './recallCta'

const PRICE_LINE = 'CA$3.99/mo · cancel anytime'

const TopIcon = ({ recall }: { recall: RecallType }) => {
  const alertType = recall.parsed.basic_details['Alert type']

  if (alertType === 'Information Update') {
    return <Icon circular name="info" />
  }
  if (
    alertType === 'Dear Healthcare Professional Letter' ||
    alertType === 'Notice to Hospitals'
  ) {
    return <Icon circular name="medkit" />
  }
  if (alertType === 'Advisory') {
    return <Icon circular name="warning" />
  }
  if (alertType === 'Public Communication') {
    return <Icon circular name="bullhorn" />
  }

  return <CategoryIcon category={recall.category[0]} circular />
}

const Recall = ({ recall }: { recall: RecallType }) => {
  const parsedPanels = Object.keys(recall.parsed)
  const unparsedPanels = recall.panels
    .filter(d => d)
    .filter(panel => !parsedPanels.includes(panel.panelName))
  const products = parsedPanels
    .filter(p => /^product_/.test(p))
    .map(key => recall.parsed[key])

  const { data: userData } = useSessionUser()
  const subscribed = userData ? isSubscriptionActive(userData) : false
  // Not gated on `loading`: the banner is in the SSR HTML and the first client
  // render (no layout shift), and is hidden once the session shows a subscriber.
  const showBanner = !subscribed
  const {
    topic: bannerTopic,
    header,
    body,
    buttonLabel,
    href: bannerHref,
  } = buildRecallCta(recall)

  const topCtaViewRef = useCtaView(
    { source: 'recall_banner', topic: bannerTopic, placement: 'top' },
    recall.apiId,
  )
  const bottomCtaViewRef = useCtaView(
    { source: 'recall_banner', topic: bannerTopic, placement: 'bottom' },
    recall.apiId,
  )

  const handleCtaClick = (placement: 'top' | 'bottom') => () => {
    trackEvent('cta_click', {
      source: 'recall_banner',
      topic: bannerTopic,
      placement,
    })
  }

  const image =
    recall.parsed?.images?.[0]?.url ??
    'https://recallmonitor.ca/product-recall-image.jpg'
  const url = buildRecallCanonical(recall.apiId)
  const parsedTitle = recall.parsed.title
  const alertType = recall.parsed?.basic_details?.['Alert type'] ?? ''

  return (
    <>
      <SEO
        title={buildRecallTitle(parsedTitle)}
        description={buildRecallDescription({
          title: parsedTitle,
          alertType,
        })}
        canonical={url}
        type="article"
        image={image}
      />
      <Container text className="mt-4">
        <Heading
          as="div"
          icon
          textAlign="center"
          // Font size before leading: tailwind-merge drops a leading-* that precedes text-[size].
          className="text-[2rem] leading-[1.28571429em]"
        >
          <TopIcon recall={recall} />
          <h1 className="m-0 text-[1em] leading-[inherit]">
            {recall.parsed.title}
          </h1>
          <Heading.Subheader className="text-[1.14285714rem] leading-[1.2em]">
            <CategoryList categories={recall.category} />
          </Heading.Subheader>
          <Heading.Subheader className="text-[1.14285714rem] leading-[1.2em]">
            <LocalDateTime
              date={recall.start_date * 1000}
              pattern="MMM do, y @ hh:mm a"
            />
          </Heading.Subheader>
        </Heading>
        <div style={{ textAlign: 'center' }}>
          <Social
            url={url}
            message={`#Recall #Alert: ${recall.parsed.title}`}
            image={image}
          />
        </div>

        <Heading as="h3">Basic Details</Heading>
        <div className="mb-4">
          <BasicDetails data={recall.parsed.basic_details} />
        </div>

        {recall.parsed.summary && (
          <>
            <Heading as="h3">Summary</Heading>
            {Object.entries(recall.parsed.summary ?? {}).map(([key, val]) => (
              <React.Fragment key={key}>
                <Heading as="h4">{key}</Heading>
                <p>{val}</p>
              </React.Fragment>
            ))}
          </>
        )}

        {showBanner && (
          <div ref={topCtaViewRef}>
            <Message info>
              <Message.Header>{header}</Message.Header>
              <p>{body}</p>
              <Link href={bannerHref} passHref>
                <Button
                  as="a"
                  primary
                  rel="nofollow"
                  onClick={handleCtaClick('top')}
                >
                  {buttonLabel}
                </Button>
              </Link>
              <p className="mt-2 text-sm">{PRICE_LINE}</p>
            </Message>
          </div>
        )}

        {recall.parsed.images && (
          <>
            <Heading as="h3">
              Images
              <Heading.Subheader>Click to enlarge</Heading.Subheader>
            </Heading>
            <Images data={recall.parsed.images} />
          </>
        )}
      </Container>

      {products.length > 0 && (
        <>
          <Container text>
            <Heading as="h3" className="!mt-[1em]">
              Products
            </Heading>
          </Container>
          <div className="flex justify-center mb-4">
            <Products data={products} />
          </div>
        </>
      )}

      <Container text className="mb-4">
        {unparsedPanels.map(panel => {
          const __html = panel?.text
            .replace(/<h2/g, '<h4')
            .replace(/<\/h2/g, '</h4')
            .replace(/\n\n/g, '<br/><br/>')

          if (!__html) return null

          return (
            <React.Fragment key={panel.panelName}>
              <Heading as="h3">{panel.title}</Heading>
              <Container
                text
                dangerouslySetInnerHTML={{
                  __html: `<div>${__html}</div>`,
                }}
                data-itsme={true}
                data-html={__html}
              />
            </React.Fragment>
          )
        })}
      </Container>

      {showBanner && (
        <Container text className="mb-4">
          <div ref={bottomCtaViewRef}>
            <Message info>
              <Message.Header>{header}</Message.Header>
              <Link href={bannerHref} passHref>
                <Button
                  as="a"
                  primary
                  rel="nofollow"
                  onClick={handleCtaClick('bottom')}
                >
                  {buttonLabel}
                </Button>
              </Link>
              <p className="mt-2 text-sm">{PRICE_LINE}</p>
            </Message>
          </div>
        </Container>
      )}
    </>
  )
}

export default Recall
